import type { Guide } from './types.js';

/**
 * The task-shaped companion to VGS_GUIDE: what to do, on which screen, and what the
 * system does in response, in the order the work actually happens — from the day a
 * task order is awarded to the day a repair is closed. It is never shown to a
 * customer account.
 */
export const VGS_STEP_GUIDE: Guide = {
  title: 'VGS step-by-step guide',
  subtitle:
    'From an awarded task order to a closed repair: set the task order up, invite the people, raise and answer work orders, get them authorized, perform them, and report on them.',
  audience:
    'Written for the VGS administrator or program manager standing a task order up, and for anyone running work orders on it day to day.',
  sections: [
    {
      id: 'before-you-start',
      title: '1. Before you start',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Work this guide in order. Each chapter assumes the previous one is done, because the platform does too: nobody can be invited to a task order that does not exist, and no work order can be raised at a facility nobody has access to.',
        },
        {
          kind: 'paragraph',
          text: 'Have these to hand before you open the application: the task order number and its contract number, the period of performance dates, the facility code and address for every facility the task order covers, the Property ID and Postal Service Area/Logistics and District/Division for each of those facilities, the local facility contact at each, the markup rate negotiated for the task order, the COR and Contracting Officer approval limits in the agreement, and the name, email, and role of every person — VGS and USPS — who needs an account.',
        },
        {
          kind: 'note',
          variant: 'warning',
          title: 'Two decisions you cannot take back easily',
          text: 'The facility code becomes part of every work order number issued at that facility, so it cannot change once work orders exist there. And an approval limit that is set too high lets a proposal be authorized by someone the agreement does not permit to authorize it — the platform routes on the limits you enter, not on the agreement it has never read.',
        },
        {
          kind: 'table',
          caption: 'Where each setup step lives',
          headers: ['What you are doing', 'Where'],
          rows: [
            [
              'Creating the program and the task order and its first facility',
              'Administration → Task orders',
            ],
            [
              'Adding more facilities to an existing task order',
              'Administration → Task orders → the task order card',
            ],
            [
              'Filling in the USPS reporting attributes of a facility',
              'Administration → Task orders → the facility → USPS reporting',
            ],
            [
              'Changing the markup rate or the response targets',
              'Administration → Task orders → Change configuration',
            ],
            ['Repair categories', 'Administration → Categories'],
            ['COR and Contracting Officer approval limits', 'Administration → Approval authority'],
            ['Inviting VGS and USPS people', 'Administration → Users'],
          ],
        },
      ],
    },
    {
      id: 'set-up-task-order',
      title: '2. Set up the awarded task order',
      blocks: [
        {
          kind: 'paragraph',
          text: 'A work order belongs to a facility, a facility belongs to a task order, and a task order belongs to a program. The program is the customer-level container, so you usually create it once and reuse it for every task order awarded under the same agreement.',
        },
        {
          kind: 'procedure',
          steps: [
            {
              do: 'Open the task order administration screen.',
              where: 'The left-hand navigation → Administration → Task orders.',
              then: 'You see every task order already on the platform, each with its markup rate, period of performance, and facilities.',
            },
            {
              do: 'Create the program, if the customer does not already have one.',
              where:
                'Add program. Enter a short uppercase Code, a Name, and pick the USPS Customer organization; the Description is optional.',
              then: 'The program becomes selectable when you add a task order. Skip this step entirely if the program you need is already listed.',
            },
            {
              do: 'Start the task order.',
              where: 'Add task order / facility.',
              then: 'One form opens that creates the task order and its first facility together, so a task order can never exist with nowhere to raise work.',
            },
            {
              do: 'Identify the task order.',
              where:
                'Program, Task order number, Name, and Contract number in the first block of the form.',
              then: 'The number is what appears on reports and in the approval authority screen, so enter it exactly as the award document does.',
            },
            {
              do: 'Enter the period of performance.',
              where: 'Period of performance start and Period of performance end.',
              then: 'Both dates are recorded against the task order and shown on its card.',
            },
            {
              do: 'Enter the markup percent and the four response targets.',
              where:
                'Markup percent, then the Response targets (hours) fields for Emergency, Urgent, Routine, and Scheduled.',
              then: 'The markup rate is applied to every proposal priced on this task order, and the response targets are what the platform measures a request against. Both can be changed later, and a change is versioned rather than overwritten.',
            },
            {
              do: 'Enter the first facility.',
              where:
                'Facility code, Facility name, Street address, City, State, ZIP code, and Time zone.',
              then: 'The facility code is upper-cased and becomes the middle segment of every work order number raised there — a facility coded TUL issues RE-TUL-000001 onwards.',
            },
            {
              do: 'Enter the facility attributes USPS reports on.',
              where:
                'Property ID, Postal Service Area/Logistics, Postal Service District/Division, Local facility contact, Contact phone, and Contact email.',
              then: 'These are copied onto every work order report row for that facility. Anything you leave blank is blank in the Contracting Officer’s report, so fill them in now rather than explaining a gap later.',
            },
            {
              do: 'Save the task order and its first facility.',
              where: 'Add task order and facility.',
              then: 'The task order appears in the list with its facility beneath it, and the facility is immediately selectable when a work order is raised.',
            },
          ],
        },
        {
          kind: 'paragraph',
          text: 'One task order can cover several facilities. Add the rest from the task order’s own card once it exists.',
        },
        {
          kind: 'procedure',
          startAt: 10,
          steps: [
            {
              do: 'Add each remaining facility.',
              where:
                'The task order’s card → Add another facility. Enter the facility code, name, city, state, and time zone.',
              then: 'The facility is created under the same task order, and therefore under the same markup rate and response targets.',
            },
            {
              do: 'Fill in the reporting attributes for each new facility.',
              where: 'The facility row → USPS reporting → the six fields → Save reporting details.',
              then: 'The facility now reports completely. You can return to this at any time; it changes the report from that point on.',
            },
          ],
        },
        {
          kind: 'note',
          variant: 'info',
          title: 'Deactivating rather than deleting',
          text: 'A facility that is no longer serviced is deactivated, not removed, so the work orders raised there keep their history. Deactivate and Reactivate are on the facility row.',
        },
      ],
    },
    {
      id: 'configure-task-order',
      title: '3. Configure the task order before anyone uses it',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Three pieces of configuration decide how work behaves on this task order: the repair categories a requestor may choose from, the markup rate applied to pricing, and the dollar limits that decide which USPS authority may authorize a proposal. Set all three before you invite anybody.',
        },
        {
          kind: 'procedure',
          steps: [
            {
              do: 'Review the repair categories.',
              where: 'Administration → Categories.',
              then: 'Every category listed here is offered to a requestor in the Repair category field on the work order form, and the category is reported to USPS as the commodity or service provided. Add what the task order covers and deactivate what it does not.',
            },
            {
              do: 'Confirm the markup rate and response targets.',
              where: 'Administration → Task orders → the task order → Change configuration.',
              then: 'Saving creates a new configuration version and requires a Reason for the change, which is written to the audit trail. Proposals already prepared keep the rate that was in effect when they were written, so a rate change never silently reprices a figure USPS has already seen.',
            },
            {
              do: 'Set the COR approval limit.',
              where:
                'Administration → Approval authority → Change a limit. Authority = USPS Contracting Officer’s Representative (COR), Task order = this task order or All task orders, Approves up to = the agreement’s COR ceiling.',
              then: 'A proposal at or below that figure is routed to a COR; above it, the platform requires the Contracting Officer instead.',
            },
            {
              do: 'Set the Contracting Officer limit and the referral threshold.',
              where:
                'The same form with Authority = USPS Contracting Officer (CO). Leave Approves up to blank for no ceiling, and enter Response Line required above.',
              then: 'Above the referral figure, the work order cannot be approved until a Facilities Response Line determination has been recorded on it, and competition applies.',
            },
            {
              do: 'Record why the limits are what they are.',
              where: 'Reason for the change, then Save limit.',
              then: 'The new limit appears in Limits in force with the time it took effect. Approvals already given keep the authority that applied when they were given, so the history stays truthful after a contract modification.',
            },
          ],
        },
        {
          kind: 'table',
          caption: 'The routing the limits produce, using the current agreement’s figures',
          headers: ['Proposal total', 'Who may authorize it', 'Additional obligation'],
          rows: [
            ['$5,000 or less', 'COR or Contracting Officer', 'None'],
            ['Above $5,000', 'COR endorses, then Contracting Officer approves', 'None'],
            [
              'Above $10,000',
              'Contracting Officer only',
              'A Facilities Response Line ticket and determination must be recorded, and competition applies',
            ],
          ],
        },
        {
          kind: 'note',
          variant: 'warning',
          title: 'What a wrong limit does',
          text: 'The platform routes on these numbers and re-checks them on the server when a decision is submitted, so a limit entered too high is not caught by anything else — it simply lets a COR authorize an amount the agreement reserves for the Contracting Officer. Check the figures against the award document before you invite approvers.',
        },
      ],
    },
    {
      id: 'invite-participants',
      title: '4. Invite the participants',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Accounts are invite-only. Nobody — including you — ever sets another person’s password: you create the account with a role and a scope, and the invitee sets their own password from a single-use link.',
        },
        {
          kind: 'procedure',
          steps: [
            {
              do: 'Open the user administration screen.',
              where: 'Administration → Users → Invite user.',
              then: 'The invitation form opens.',
            },
            {
              do: 'Identify the person.',
              where: 'Full name, Email, Title, and Phone.',
              then: 'The title is recorded alongside anything they approve, which is why an approver’s title should read as the agreement does — Contracting Officer, not Contracts.',
            },
            {
              do: 'Choose the organization, then the role.',
              where: 'Organization — each is labelled USPS or VGS — then Role.',
              then: 'The role list narrows to the roles that organization type may hold, so a USPS account cannot be given a VGS role or the reverse.',
            },
            {
              do: 'Set the scope.',
              where:
                'Task order access selects whole task orders and grants every facility under them. Additional facility access is only for facilities outside those task orders.',
              then: 'The account sees work orders, reports, and approvals for the facilities in its scope and nothing else. For a normal task order participant, selecting the task order is all that is needed.',
            },
            {
              do: 'Create the invitation.',
              where: 'Create user and invitation.',
              then: 'The account is created in a pending state, an invitation email is sent, and the single-use link is displayed once on screen with a Copy link button. It is not recoverable afterwards — if you lose it, re-invite.',
            },
            {
              do: 'Repeat for every participant.',
              where: 'The same form.',
              then: 'The user list shows Invitation pending until the person activates, and their last sign-in afterwards.',
            },
          ],
        },
        {
          kind: 'table',
          caption: 'Who to invite, and as what',
          headers: ['Person', 'Organization', 'Role to give them'],
          rows: [
            ['The VGS program manager for the task order', 'VGS', 'VGS Program Manager'],
            ['VGS facility managers and technicians', 'VGS', 'VGS Facility Manager or VGS Staff'],
            ['USPS personnel who raise repair requests', 'USPS', 'USPS Requestor'],
            ['The Contracting Officer’s Representative', 'USPS', 'USPS COR'],
            ['The Contracting Officer', 'USPS', 'USPS CO'],
            ['Anyone who only reads — oversight, FSCMC, audit', 'Either', 'Read-only / Auditor'],
          ],
        },
        {
          kind: 'note',
          variant: 'info',
          title: 'No VGS role can approve a proposal',
          text: 'Approval authority belongs to USPS and no VGS role holds the approval permission at all. There is nothing to be careful about when choosing a VGS role: none of them can authorize VGS’s own price.',
        },
        {
          kind: 'bullets',
          items: [
            'Re-invite issues a fresh single-use link and invalidates the old one — use it when an invitation expired or never arrived.',
            'Access reopens the role and scope of an existing account, for when someone changes job or a new facility is added to their task order.',
            'Deactivate removes an account’s access without touching anything it did; Reactivate restores it.',
            'A local facility contact recorded against a facility is contact information, not an account. They receive no system email unless they are also invited as a user.',
          ],
        },
      ],
    },
    {
      id: 'vgs-raises-work-order',
      title: '5. Raise a work order',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Only VGS enters work orders. USPS reports a problem to VGS by telephone or email, and you record it here so that every request, from first report to close, sits inside the system. USPS users can see and follow the work order as soon as it exists, but they cannot raise one themselves.',
        },
        {
          kind: 'procedure',
          steps: [
            {
              do: 'Open the work order form.',
              where: 'New work order, from the dashboard or the work order list.',
              then: 'The form opens with the point of contact blank.',
            },
            {
              do: 'Record who reported the problem.',
              where: 'The Point of contact block: Name, Email, Phone.',
              then: 'This is the USPS person VGS calls about access and scheduling, and it is reported as the local facility contact. Enter the person who asked, not yourself.',
            },
            {
              do: 'Classify the request.',
              where: 'Facility, Repair category, Priority, and Requested completion date.',
              then: 'The facility decides the work order number and the reporting attributes; the priority decides which response target applies.',
            },
            {
              do: 'Locate the problem.',
              where: 'Building or area, Specific location, and Equipment or asset.',
              then: 'These are optional, but they are what a technician reads before travelling, so record what the caller told you.',
            },
            {
              do: 'Describe the problem.',
              where: 'Short title, Problem description, and Additional comments.',
              then: 'The title is what appears in lists; the description should carry what is wrong, when it started, and the effect on operations. Access constraints belong in the comments.',
            },
            {
              do: 'Submit.',
              where: 'Submit work order.',
              then: 'A work order number is issued, the request lands in Submitted, and the VGS users scoped to that facility are emailed and notified in the application.',
            },
            {
              do: 'Attach the photographs, if the caller sent any.',
              where:
                'The confirmation screen offers an upload immediately; Documents & comments on the work order does the same later.',
              then: 'The number is issued before any upload, so a failed attachment never loses the request.',
            },
          ],
        },
      ],
    },
    {
      id: 'respond-to-work-order',
      title: '6. Respond to a work order and get it authorized',
      blocks: [
        {
          kind: 'paragraph',
          text: 'This is the chapter you will use every day. A submitted work order needs acknowledging, investigating, pricing, and submitting for a USPS decision — and no work may be performed until that decision is an approval.',
        },
        {
          kind: 'procedure',
          steps: [
            {
              do: 'Acknowledge the request.',
              where: 'Open the work order → Acknowledge, top right.',
              then: 'The status moves to Under VGS Review and the acknowledgement time is recorded, which is what the response target is measured against.',
            },
            {
              do: 'Assign it to whoever will run it.',
              where: 'Overview tab → VGS assignment → Assigned to.',
              then: 'The work order appears in that person’s “assigned to me” view. This panel is VGS-only.',
            },
            {
              do: 'Move it into estimating.',
              where: 'Begin estimate, top right.',
              then: 'The status becomes Estimate in Progress, which is what USPS sees while you scope the repair.',
            },
            {
              do: 'Record what you found.',
              where:
                'Investigation tab → Date inspected, Estimated labor hours, Findings, Root cause, Recommended action, and the subcontractor checkbox.',
              then: 'Findings, root cause, and recommended action are shown to USPS. Anything you would not want to explain to the Contracting Officer belongs in VGS internal notes on the same screen, which USPS never sees.',
            },
            {
              do: 'Price the repair.',
              where:
                'Proposal tab → Prepare proposal. Write the Scope of work and the Findings summary, then add a cost item per line of work with its Category, Description, Qty, Unit, and Unit price.',
              then: 'Customer-visible lines make up the price USPS is asked to authorize; the task order’s markup rate is applied by the server when the version is saved. A line marked internal is excluded from the customer total and never shown.',
            },
            {
              do: 'Attach the backup the price rests on.',
              where:
                'Documents & comments → Upload files, with Document type = Subcontractor quote or Proposal backup.',
              then: 'A proposal carrying a subcontractor line requires a quote on file. Mark a quote customer-visible if USPS is to see it as part of the authorization.',
            },
            {
              do: 'Save the draft and read it as USPS will.',
              where: 'Save draft, then the version card on the Proposal tab.',
              then: 'Everything outside the VGS internal notes panel is what USPS will read. Edit draft until it says what you mean.',
            },
            {
              do: 'Submit it for a decision.',
              where: 'Submit to USPS.',
              then: 'The work order moves to Awaiting USPS Approval, the version is locked, and the USPS authority whose limit covers the total is emailed. The version card shows which authority that is.',
            },
          ],
        },
        {
          kind: 'paragraph',
          text: 'Above the referral threshold two further obligations appear on the Proposal tab, and the approval is blocked until each is satisfied.',
        },
        {
          kind: 'definitions',
          items: [
            {
              term: 'USPS Facilities Response Line',
              description:
                'Above the configured threshold, USPS personnel must call the Facilities Response Line at 855-444-6375, obtain a ticket, and have the determination recorded on the work order — ticket number, date and time of the call, and whether the repair qualifies as a project. If it does, it leaves the Reimbursable Expense process altogether and the work order is cancelled once USPS confirms how the project will proceed. If the Response Line declines the work, it may still be authorized at the Contracting Officer’s discretion, recorded on the same panel.',
            },
            {
              term: 'Competition',
              description:
                'Above the same threshold, or whenever the Contracting Officer requires it, the minimum number of quotes must be on file. With fewer, use Request competition exception on the Competition panel and justify why adequate competition could not be obtained; the Contracting Officer approves or rejects that exception, and only an approval unblocks the authorization.',
            },
          ],
        },
        {
          kind: 'note',
          variant: 'warning',
          title: 'Do not start work on a verbal go-ahead',
          text: 'The approval record is the artifact of authorization and the platform will not let you schedule until it exists. If USPS has told you to proceed, ask them to record the decision in the application first; it takes them under a minute and it is what protects the invoice.',
        },
      ],
    },
    {
      id: 'after-the-decision',
      title: '7. After the USPS decision: perform, document, close',
      blocks: [
        {
          kind: 'paragraph',
          text: 'A decision arrives by email and changes the work order in the application. There are three of them, and each has one sensible next action.',
        },
        {
          kind: 'table',
          headers: ['Decision', 'What it means', 'What you do'],
          rows: [
            [
              'Revision requested',
              'USPS wants the proposal changed. Their comment is required and is shown on the version.',
              'Proposal tab → Prepare revision. The new version carries a new number; the old one stays in the record.',
            ],
            [
              'Rejected',
              'USPS does not want the work performed at that price.',
              'Read the comment. Either prepare a revision, or cancel the work order with a reason if the repair is not proceeding.',
            ],
            [
              'Approved → Procurement Stage',
              'VGS is authorized to perform the work at the approved price. The approval is permanent; the work order waits in Procurement Stage while the PR/PO (or P-card) is recorded.',
              'Administrators record the PR and PO under Schedule & completion → Procurement. Then schedule the repair and perform it (Emergency/Urgent work is not held up by procurement).',
            ],
          ],
        },
        {
          kind: 'procedure',
          steps: [
            {
              do: 'Schedule the repair.',
              where:
                'Schedule & completion tab → Scheduled date, Time window, and Notes for USPS. Subcontractor and internal notes are in the VGS-only panel below.',
              then: 'The work order moves to Repair Scheduled and the date, window, and USPS-visible notes are shown to the facility.',
            },
            {
              do: 'Start the work on the day.',
              where: 'Start work, top right.',
              then: 'The status becomes Work in Progress and the actual start is recorded, which is the performance start date on the USPS report.',
            },
            {
              do: 'Place it on hold if something stops you.',
              where: 'Place on hold, with a reason.',
              then: 'A banner on the work order carries your reason, and resuming returns it to exactly the status the hold interrupted rather than to the beginning.',
            },
            {
              do: 'Attach completion photographs if USPS has asked for them (optional).',
              where: 'Documents & comments → Upload files, with Document type = Completion photo.',
              then: 'At least one is required: the platform refuses to mark a work order complete without one, so the completion record is evidenced rather than asserted.',
            },
            {
              do: 'Document what was done.',
              where:
                'Schedule & completion → Document completion → Date and time completed, Work performed, Final notes for USPS, Follow-up recommendation, and Exceptions noted.',
              then: 'All of those are shown to USPS. Anything commercially sensitive goes in the VGS internal completion notes panel on the same form.',
            },
            {
              do: 'Mark it complete.',
              where:
                'The “Mark this work order complete and notify USPS” checkbox, then Save completion record.',
              then: 'The status becomes Completed, the completion time is recorded, and USPS is notified.',
            },
            {
              do: 'Record the invoice number.',
              where: 'Overview tab → Invoice → Invoice number → Save invoice number.',
              then: 'The number is visible to USPS and appears in the work order report, which is how a report row is reconciled to a billed invoice.',
            },
            {
              do: 'Close the work order.',
              where: 'Close work order, top right. A reason is offered and is worth giving.',
              then: 'The status becomes Closed. The record stops changing, no further documents can be removed, and it settles into the reporting and retention history.',
            },
          ],
        },
        {
          kind: 'note',
          variant: 'info',
          title: 'Scope that grows mid-repair',
          text: 'If the work turns out to be larger than what was authorized, do not absorb it into the same approved figure. Prepare a revised proposal; if the new total crosses a threshold, the platform re-routes it to the authority that may approve the higher amount.',
        },
      ],
    },
    {
      id: 'reports-and-audit',
      title: '8. Report on it, and prove it afterwards',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The Contracting Officer may ask for reporting monthly, quarterly, annually, or at any other time, in Postal Service fields. Everything they need comes off the Reports screen, and you can produce it for them or leave them to pull it themselves.',
        },
        {
          kind: 'procedure',
          steps: [
            {
              do: 'Open the work order report.',
              where: 'Reports → the Work orders tab.',
              then: 'Every work order in your scope is listed with its Postal Service reporting fields and its cycle times.',
            },
            {
              do: 'Set the period.',
              where: 'Submitted from and Submitted to.',
              then: 'A month, a quarter, a fiscal year, or any range in between — the cadence is a date filter, not a different report. Clear resets the filters.',
            },
            {
              do: 'Narrow to one facility if that is what was asked for.',
              where: 'Facility.',
              then: 'Only that facility’s work orders are reported.',
            },
            {
              do: 'Choose the columns the Contracting Officer asked for.',
              where:
                'Choose columns, then tick the fields; Reset returns to reporting every column.',
              then: 'The same choice drives the table on screen and the exported file, so what you send matches what you checked.',
            },
            {
              do: 'Export it.',
              where: 'Export CSV, top right.',
              then: 'A CSV downloads that opens directly in Excel, with numbers still numeric so they can be totalled.',
            },
            {
              do: 'Produce the approval backup for an invoice.',
              where: 'Reports → the Approvals tab, with the decided-date filters.',
              then: 'Every approval decision in the period is listed with the decider, the authority they used, the amount authorized, and the Response Line and competition position — which is the backup the monthly invoice needs.',
            },
          ],
        },
        {
          kind: 'paragraph',
          text: 'When a specific decision is questioned months later, the answer is on the work order rather than in anyone’s memory.',
        },
        {
          kind: 'bullets',
          items: [
            'The History tab on a work order lists every status change, approval, comment, and document in order, with who did it and when.',
            'The approval record on the Proposal tab shows the decision, the decider, the authority they held at the time, and the amount authorized.',
            'The Audit log, for roles that hold it, spans every work order rather than one.',
            'Nothing in that history is edited or overwritten; corrections are appended, so the record explains itself.',
          ],
        },
      ],
    },
  ],
};
